<script lang="ts">
	import { sectionComponents } from '$lib/types/sections';
	import type { CourseDetailStore } from '$houdini';
	import CourseCardsBar from '$lib/components/Bar/CourseCardsBar.svelte';
	import FeaturesBox from '$lib/components/Box/FeaturesBox.svelte';
	import { getTranslate } from '@tolgee/svelte';
	import { page } from '$app/stores';
	import { getPathToEnterCourseBySession } from '$lib/service/navigation';
	import { browser } from '$app/environment';
	import HeroDivided from '$lib/components/PageSection/HeroDivided.svelte';
	import CourseDetailHeroSection from '$lib/components/Course/CourseDetailHeroSection.svelte';
	import HeroTrailerOverlay from '$lib/components/Video/Overlays/HeroTrailerOverlay.svelte';
	import SideNavigation from '$lib/components/Navigation/SideNavigation.svelte';
	import { onMount } from 'svelte';
	import GradientOverview from '$lib/components/Decorations/GradientOverview.svelte';
	import GradientCurriculum from '$lib/components/Decorations/GradientCurriculum.svelte';
	import GradientTeachers from '$lib/components/Decorations/GradientTeachers.svelte';
	import { isSafari } from '$lib/utils';
	import MetaHead from '$lib/components/SEO/MetaHead.svelte';
	import * as walker from '@elbwalker/walker.js';
	import { PUBLIC_ENVIRONMENT } from '$env/static/public';
	import { buildCourseSchema } from '../../../lib/schemaOrg';

	/** @type {import('./$types').LayoutData} */
	export let data: { CourseDetail: CourseDetailStore };

	const { t } = getTranslate();
	const tolgeePrefix = 'course-detail-page';

	let trailerOverlayOpen: boolean = false;
	let currentSectionId = '';
	let sectionComponentsProps: Record<ArrayElementOf<typeof sectionComponents>['id'], any>;
	let sectionElements: Array<Element> = [];

	let observer: IntersectionObserver;

	$: ({ CourseDetail } = data);
	$: course = $CourseDetail.data?.course;
	$: nextLessonPath = getPathToEnterCourseBySession($page.data.session, course);
	$: pathToFeaturedSignUp = new URL(
		$page.data.session?.user?.id
			? `${$page.url.origin}${nextLessonPath}`
			: `${$page.url.origin}/auth/signup?course=${course?.slug}`
	).toString();

	$: if (course && browser && PUBLIC_ENVIRONMENT.includes('prd')) {
		walker.elb('walker run');
	}

	$: courseSlugAndLessonsProp = {
		courseSlug: course?.slug ?? '',
		lessons: course?.lessons ?? []
	};

	$: sectionComponentsProps = {
		Overview: {
			description: course?.outline,
			...courseSlugAndLessonsProp,
			trailerThumbnail: course?.featuredThumbnail?.lg
		},
		Who: { cards: course?.audiences },
		Curriculum: { ...courseSlugAndLessonsProp, trailerThumbnail: course?.featuredThumbnail?.lg },
		Takeaways: {
			highlights: course?.keyTakeaways,
			imageUrls: course?.imagesGrid?.images.map((image) => {
				return image.lg;
			})
		},
		Why: { cards: course?.reasons },
		Teachers: { cards: course?.instructors }
	};

	function handleSideNavigationClick(event: CustomEvent<{ id: string }>) {
		const sectionElement = document.getElementById(event.detail.id);
		if (!sectionElement) return;

		if (browser) {
			window.scrollTo({
				top: sectionElement.offsetTop - 88,
				behavior: 'smooth'
			});
		}
	}

	function handleSectionIntersecting(id: string) {
		currentSectionId = id;
	}

	function initObserver() {
		observer = new IntersectionObserver(
			(entries) => {
				for (let entry of entries) {
					if (entry.isIntersecting) {
						handleSectionIntersecting(entry.target.id);
						break;
					}
				}
			},
			{
				threshold: 0.55,
				rootMargin: '0% 0% -30% 0%'
			}
		);

		sectionElements = Array.from(document.querySelectorAll('.course-detail-section'));

		for (let element of sectionElements) {
			observer.observe(element);
		}
	}

	onMount(() => {
		initObserver();

		return () => {
			observer.disconnect();
		};
	});
</script>

<MetaHead
	title={course?.name}
	description={course?.shortDescription}
	publishedDate={course?.publishedDate}
	metaTitle={course?.seo?.metaTitle}
	metaDescription={course?.seo?.metaDescription}
	ogUrl={$page.url.toString()}
	ogTitle={course?.seo?.ogTitle}
	ogDescription={course?.seo?.ogDescription}
	ogImage={course?.seo?.ogImage?.url}
	image={course?.featuredThumbnail?.social}
	useSchemaOrg={true}
	schema={buildCourseSchema(course)}
/>

{#if trailerOverlayOpen && course}
	<HeroTrailerOverlay on:close={() => (trailerOverlayOpen = false)} {course} />
{/if}

<!-- isSafari is a quickfix to hide the blurred gradients looking bad on Safari -->
{#if !isSafari() && course && course.name && course.instructors && course.instructors.length > 0}
	<div class="overflow-hidden h-fit">
		<GradientOverview
			additionalClass="absolute lg:max-w-[870px] lg:h-[1575px] right-0 lg:pl-44 xl:pl-0 top-[1300px] lg:top-[650px] !overflow-hidden -z-10"
		/>

		<GradientCurriculum
			additionalClass="absolute lg:max-w-[1070px] lg:h-[1575px] right-0 lg:pl-44 xl:pl-0 top-[2600px] lg:top-[2250px] !overflow-hidden -z-10"
		/>
		<GradientTeachers
			additionalClass="absolute hidden 2xl:block w-[800px] xl:max-w-[1870px] h-[450px] xl:h-[1375px] pl-96 lg:pl-0 right-0 top-[5100px] lg:top-[3500px] !overflow-hidden -z-10"
		/>
	</div>
{/if}

<div
	class="grid grid-cols-12 gap-0 content-start isolate"
	data-elb="course"
	data-elbaction="load:viewed "
	data-elb-course="course_name:{course?.name};course_slug:{browser
		? window.location.pathname
		: ''};course_skillevel:{course?.skillLevel}"
>
	<div class="col-start-10 col-end-13 min-w-fit">
		<!-- isSafari is a quickfix to hide the blurred gradients looking bad on Safari -->
		<div class="overflow-hidden h-fit w-[500px] relative">
			<!-- <GradientOverview
				additionalClass="absolute lg:max-w-[870px] lg:h-[1575px] left-100 top-[1300px] lg:top-[650px] !overflow-hidden"
			/>
			<GradientCurriculum
				additionalClass="absolute lg:max-w-[1070px] lg:h-[1575px] right-0 lg:pl-44 xl:pl-0 top-[2600px] lg:top-[2250px] !overflow-hidden -z-10"
			/>
			<GradientTeachers
				additionalClass="absolute w-[800px] lg:max-w-[1870px] h-[500px] lg:h-[1375px] lg:pl-44 xl:pl-0 right-0 top-[5100px] sm:top-[5000px] lg:top-[3500px] !overflow-hidden -z-10"
			/> -->
		</div>
	</div>

	<div class="col-start-1 col-end-13">
		<div class="grid grid-cols-12">
			<div class="col-start-1 col-end-13">
				<HeroDivided
					additionalClass="bg-course-detail-hero bg-cover bg-origin-padding pb-[0.025rem]"
				>
					{#if course}
						<CourseDetailHeroSection
							on:showOverlay={() => (trailerOverlayOpen = true)}
							additionalClass="col-start-2 col-end-6"
							title={course?.name || ''}
							subtitle={course?.shortDescription || ''}
							buttonText={$t(`${tolgeePrefix}.hero.button`)}
							href={pathToFeaturedSignUp}
						/>
						<div class="w-full px-11 h-[1px] pb-11">
							<hr class="flex md:hidden mt-11 w-full h-[1px] border-none bg-black" />
						</div>

						<FeaturesBox
							additionalClass="col-start-8 col-end-12 border border-black text-black 2xl:w-fit mt-0 justify-center items-center justify-self-center self-center !gap-11 !flex-col sm:!flex-row md:!flex-col 2xl:!flex-row"
							elementId="features-section"
							timeToComplete={course?.timeToComplete ?? null}
							skillLevel={course?.skillLevel ?? null}
							prerequisites={course?.prerequisites?.length
								? course?.prerequisites.map((p) => p.title)
								: []}
							enableAnimation={false}
						/>
					{:else}
						<div class="h-[34rem]" />
					{/if}
				</HeroDivided>
			</div>

			<div class="col-start-2 col-end-12 xl:col-end-9 2xl:col-end-8 3xl:col-end-7 mt-32">
				<div class="flex flex-col gap-32">
					{#each sectionComponents as { id, component }, i}
						<div class="course-detail-section" {id}>
							<svelte:component this={component} {...sectionComponentsProps[id]} />
						</div>
					{/each}
				</div>
			</div>

			<div
				class="h-full hidden xl:grid col-start-9 col-end-12 lg:col-start-10 md:col-end-13 mt-32 pr-11"
			>
				<div class="flex flex-col h-full w-full">
					<div
						class="flex justify-center items-center sticky top-44 p-4 max-w-[290px] rounded-xl border-black border-[1px] bg-white"
					>
						<SideNavigation
							on:clickLink={handleSideNavigationClick}
							{currentSectionId}
							title={course?.name}
							entries={sectionComponents.map(({ id }) => {
								return {
									id: id,
									title: $t(`${tolgeePrefix}.${id}.title`)
								};
							})}
							button={{
								title: $t(`${tolgeePrefix}.side-navigation.button`),
								href: pathToFeaturedSignUp
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="pt-44 col-start-2 col-end-12">
		<CourseCardsBar
			elementId="featured-cards-section"
			titleTolgeeKey={`${tolgeePrefix}.courseCardsBar.title`}
			cards={course?.relatedVideoCourse}
		/>
	</div>
</div>
